import { useTranslation } from "react-i18next";
import type { FieldErrors } from "react-hook-form";
import type { GuideInterviewItem } from "@ckgo2/shared/types";
import {
  addInterviewItem,
  moveInterviewItem,
  removeInterviewItem,
  updateInterviewItem,
} from "./guideProfileModel";

/**
 * Rozhovor „Ptali jsme se“ v editoru průvodce: uspořádaný seznam otázka a odpověď (přidat,
 * odebrat, posunout tlačítky). Prezentační komponenta: vazbu na formulář (`Controller`)
 * a nápovědu pole drží stránka `GuideEditPage` (měřidlo nápověd měří jen stránku).
 *
 * Texty jsou prostý text (`GuideInterviewItem`), proto `<textarea>`, ne `RichTextEditor`
 * (`FaqEditor` zapisuje HTML). Klíč položky je index jako ve `FaqEditor`: hodnoty jsou řízené,
 * takže posun přesune text správně. Prázdné řádky editor potichu nezahazuje; uložení je
 * zablokuje resolver (`interview.N.question`) a pole se zbarví.
 *
 * Háčky E2E: kořen `data-rozhovor`, položka `data-rozhovor-polozka`, tlačítka
 * `data-posun="nahoru|dolu"`, `data-odebrat`, `data-pridat-otazku`, pole `data-otazka`,
 * `data-odpoved`.
 */
/**
 * Chyby položek rozhovoru, jak je vrací `formState.errors.interview` formuláře s polem
 * `interview: GuideInterviewItem[]`. Typ stojí nad tím, co komponenta dostává, ne nad
 * formulářem stránky, aby komponenta neznala tvar celého editoru.
 */
export type GuideInterviewErrors = FieldErrors<{ interview: GuideInterviewItem[] }>["interview"];

interface Props {
  value: GuideInterviewItem[] | undefined;
  onChange: (items: GuideInterviewItem[]) => void;
  /** Chyby položek z `formState.errors.interview`. */
  errors?: GuideInterviewErrors;
}

const moveButtonClass =
  "rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700";

function ArrowIcon({ path }: { path: string }) {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

export default function GuideInterviewEditor({ value, onChange, errors }: Props) {
  const { t } = useTranslation();
  const items = value ?? [];

  return (
    <div data-rozhovor="" className="space-y-3">
      {items.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("guideProfile.interview.empty")}
        </p>
      )}

      {items.map((item, index) => {
        const itemErrors = errors?.[index];
        const questionError = itemErrors?.question?.message;
        const answerError = itemErrors?.answer?.message;
        const questionId = `guide-interview-${index}-question`;
        const answerId = `guide-interview-${index}-answer`;
        return (
          <div
            key={index}
            data-rozhovor-polozka=""
            className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t("guideProfile.interview.itemN", { number: index + 1 })}
                </span>
                <button
                  type="button"
                  onClick={() => onChange(moveInterviewItem(items, index, -1))}
                  disabled={index === 0}
                  data-posun="nahoru"
                  className={moveButtonClass}
                  title={t("common.actions.moveUp")}
                  aria-label={t("common.actions.moveUp")}
                >
                  <ArrowIcon path="M5 15l7-7 7 7" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(moveInterviewItem(items, index, 1))}
                  disabled={index >= items.length - 1}
                  data-posun="dolu"
                  className={moveButtonClass}
                  title={t("common.actions.moveDown")}
                  aria-label={t("common.actions.moveDown")}
                >
                  <ArrowIcon path="M19 9l-7 7-7-7" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => onChange(removeInterviewItem(items, index))}
                data-odebrat=""
                className="text-sm text-red-500 hover:text-red-700"
              >
                {t("common.actions.remove")}
              </button>
            </div>

            <div>
              <label htmlFor={questionId} className="label">
                {t("guideProfile.interview.question")}
              </label>
              <input
                id={questionId}
                type="text"
                value={item.question ?? ""}
                onChange={(e) =>
                  onChange(updateInterviewItem(items, index, "question", e.target.value))
                }
                className={`input ${questionError ? "input-error" : ""}`}
                placeholder={t("guideProfile.interview.questionPlaceholder")}
                data-otazka=""
              />
              {questionError && <p className="text-error-600 mt-1 text-sm">{questionError}</p>}
            </div>

            <div>
              <label htmlFor={answerId} className="label">
                {t("guideProfile.interview.answer")}
              </label>
              <textarea
                id={answerId}
                rows={4}
                value={item.answer ?? ""}
                onChange={(e) =>
                  onChange(updateInterviewItem(items, index, "answer", e.target.value))
                }
                className={`input ${answerError ? "input-error" : ""}`}
                placeholder={t("guideProfile.interview.answerPlaceholder")}
                data-odpoved=""
              />
              {answerError && <p className="text-error-600 mt-1 text-sm">{answerError}</p>}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => onChange(addInterviewItem(items))}
        data-pridat-otazku=""
        className="btn btn-secondary"
      >
        {t("guideProfile.interview.add")}
      </button>
    </div>
  );
}
